// src/pages/ClassPage.js
// PUBLIC, shareable class page — the canonical link Danielle sends anywhere
// (cold marketing, member nudges, social). One URL (/class/:id) that adapts to
// the viewer and the class's access level. See docs/decisions/adr-public-class-page.md.
//
// Behavior matrix (resolved here):
//   ┌─────────────────────────┬──────────────────────────┬──────────────────────────┐
//   │ viewer                  │ FREE class               │ PAID (members-only) class │
//   ├─────────────────────────┼──────────────────────────┼──────────────────────────┤
//   │ logged-out / non-member │ plays inline + Join CTA  │ teaser + Join CTA         │
//   │ member / admin / friend │ → handed into the app    │ → handed into the app     │
//   └─────────────────────────┴──────────────────────────┴──────────────────────────┘
//
// Security: this page never receives a paid class's playback id. `/api/public-class`
// only returns `muxPlaybackId` for free classes, so a paywalled video can't be
// played here regardless of viewer state — members watch paid classes in the
// authenticated app (the hand-off below).
//
// Conversion mechanics mirror /join exactly (account-first → subscribe):
//   signed-out → SignUpButton modal → sessionStorage-TTL intent survives the
//   Clerk remount → auto-fire checkout once authed. See the intent helpers below.
//
// Paint-first: no visibility gate. We render immediately (hero skeleton, then
// real content) so a cold visitor never stares at a blank screen.

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser, useAuth, SignUpButton, SignInButton } from '@clerk/clerk-react';
import { hasActiveSubscription } from '../services/classAccess';
import { markWelcomePending } from '../services/welcomeMoment';
import { PlanOptions, useAnnualOffer } from '../components/PlanPicker';
import { resolvePlanCopy, rememberPlanChoice, consumePlanChoice } from '../services/subscriptionPlans';
import { fetchSiteContent, getCachedSiteContent } from '../services/firebase';
import FormattedText from '../components/FormattedText';

const MUX_PLAYER_CDN = 'https://cdn.jsdelivr.net/npm/@mux/mux-player@3/dist/mux-player.min.js';

// ── Admin-editable copy with hardcoded fallbacks ───────────────────────────
// Every user-facing string is overridable via siteConfig (AdminDashboard) but
// renders legibly from these defaults even if Firestore is unreachable. Copy is
// invitational per feedback_invitational_copy — an invitation in, never a
// remediation pitch. No emoji per feedback_no_emoji_icons.
const COPY = {
    classPageEyebrow: 'A CLASS WITH DANIELLE',
    classPageFreeBadge: 'FREE CLASS',
    classPageJoinHeading: 'Loved that?',
    classPageJoinBody: 'This is one of many. Members get the full on-demand library plus live classes every week.',
    classPagePaidHeading: 'This class is part of the membership',
    classPagePaidBody: 'Join to unlock this class, the full on-demand library, and live classes every week.',
    classPageJoinCtaLabel: 'Join the membership',
    classPageSignInLabel: 'Already a member? Sign in',
    classPageExploreLabel: 'Explore the membership',
    classPageOpeningLabel: 'Opening your library…',
    classPageActivatingLabel: 'Activating your membership…',
    classPageUnavailableHeading: 'This class isn’t available',
    classPageUnavailableBody: 'It may have been moved or is still being prepared. Explore everything else inside.',
    subscriptionPriceLabel: '$15/mo',
};

function copyFrom(content, key) {
    const v = content && content[key];
    return (typeof v === 'string' && v.trim()) ? v : COPY[key];
}

// ── Subscribe-intent (account-first) — mirrors /join's proven approach ──────
// The viewer clicks "Join" while signed-out; we flip a ref AND write a
// sessionStorage timestamp before opening Clerk's modal. The ref is the fast
// signal for the sign-UP path (modal closes in place); the sessionStorage
// timestamp survives the remount Clerk's sign-IN path can trigger. 5-min TTL
// self-cleans an abandoned intent. Distinct key from /join so the two pages
// never cross-fire.
const CLASS_SUBSCRIBE_INTENT_KEY = 'mom_class_subscribe_intent_at';
const CLASS_SUBSCRIBE_INTENT_TTL_MS = 5 * 60 * 1000;

function markClassSubscribeIntent() {
    try { sessionStorage.setItem(CLASS_SUBSCRIBE_INTENT_KEY, String(Date.now())); } catch { /* unavailable */ }
}
function consumeFreshClassSubscribeIntent() {
    try {
        const raw = sessionStorage.getItem(CLASS_SUBSCRIBE_INTENT_KEY);
        if (!raw) return false;
        const ts = parseInt(raw, 10);
        if (!ts || Number.isNaN(ts)) { sessionStorage.removeItem(CLASS_SUBSCRIBE_INTENT_KEY); return false; }
        if (Date.now() - ts > CLASS_SUBSCRIBE_INTENT_TTL_MS) {
            sessionStorage.removeItem(CLASS_SUBSCRIBE_INTENT_KEY);
            return false;
        }
        return true;
    } catch { return false; }
}
function clearClassSubscribeIntent() {
    try { sessionStorage.removeItem(CLASS_SUBSCRIBE_INTENT_KEY); } catch { /* unavailable */ }
}

// Per-instance appearance for this page's SignUpButton — keeps the returning-
// member "Sign In" CTA prominent (same rationale as JOIN-002). Deep-merges with
// the global Clerk appearance; scoped to this instance only.
const signUpModalAppearance = {
    elements: {
        footerActionLink: { fontWeight: 700, textDecoration: 'underline' },
    },
};

// ══════════════════════════════════════════════════════════════════════════
//  Presentational pieces (module scope — never nested, per no-inner-components)
// ══════════════════════════════════════════════════════════════════════════

function PageShell({ children }) {
    return (
        <div style={{ background: 'var(--mom-cream)', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <div className="mom-atmosphere" />
            <div style={{ maxWidth: 880, margin: '0 auto', padding: 'clamp(24px, 5vw, 56px) clamp(16px, 4vw, 32px)' }}>
                {/* Brand wordmark — quiet, links home for orientation */}
                <a href="/" style={{ textDecoration: 'none', display: 'inline-block', marginBottom: 28 }}>
                    <span style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 20, color: 'var(--mom-charcoal)' }}>
                        Moments of Movement
                    </span>
                </a>
                {children}
            </div>
        </div>
    );
}

function PlayerFrame({ playbackId }) {
    return (
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: 20, boxShadow: 'var(--shadow-lg)', background: '#000' }}>
            <mux-player
                playback-id={playbackId}
                stream-type="on-demand"
                playsinline
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', '--media-object-fit': 'contain' }}
            />
        </div>
    );
}

function PosterFrame({ thumbnailUrl, title, locked }) {
    return (
        <div style={{
            position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden',
            borderRadius: 20, boxShadow: 'var(--shadow-lg)',
            background: thumbnailUrl ? '#000' : 'linear-gradient(135deg, var(--mom-charcoal), var(--mom-charcoal-light))',
        }}>
            {thumbnailUrl && (
                <img src={thumbnailUrl} alt={title || ''} referrerPolicy="no-referrer"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: locked ? 0.5 : 0.85 }} />
            )}
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.45) 100%)' }} />
            {locked && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.92 }}>
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                </div>
            )}
        </div>
    );
}

function MetaLine({ cls, content }) {
    const bits = [];
    if (cls.length) bits.push(`${cls.length} min`);
    if (cls.isFree) bits.push(copyFrom(content, 'classPageFreeBadge'));
    if (!bits.length) return null;
    return (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            {bits.map((b, i) => (
                <span key={i} style={{
                    fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
                    padding: '4px 12px', borderRadius: 20,
                    background: i === 1 ? 'color-mix(in srgb, var(--mom-primary) 14%, transparent)' : 'var(--mom-white)',
                    color: i === 1 ? 'var(--mom-primary-dark)' : 'var(--mom-charcoal)',
                    boxShadow: 'var(--shadow-sm)',
                }}>{b}</span>
            ))}
        </div>
    );
}

function CenteredNote({ label }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: '40vh' }}>
            <div style={{ width: 22, height: 22, borderRadius: '50%', border: '2.5px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)' }}>{label}</span>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════════
//  Main page
// ══════════════════════════════════════════════════════════════════════════

function ClassPage({ demoMode = false }) {
    const { id } = useParams();
    const navigate = useNavigate();

    // Defensive Clerk hooks (matches JoinPage/Account). In demo mode Clerk isn't
    // mounted, so guard against throwing.
    let isLoaded = true;
    let isSignedIn = false;
    let user = null;
    let getToken = async () => null;
    if (!demoMode) {
        try {
            const u = useUser();
            const a = useAuth();
            isLoaded = u.isLoaded;
            isSignedIn = u.isSignedIn;
            user = u.user;
            getToken = a.getToken;
        } catch (e) { /* fall through to defaults */ }
    }

    const [content, setContent] = useState(() => getCachedSiteContent() || {});
    // Yearly plan choice (docs/ux/annual-plan.md). null ⇒ the conversion panel
    // is exactly today's: one price in the sentence, monthly checkout.
    const annualOffer = useAnnualOffer(!demoMode && content.enableAnnualPlan === true);
    const [selectedPlan, setSelectedPlan] = useState('annual');
    const [cls, setCls] = useState(null);      // resolved class metadata
    const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'notfound' | 'error'
    const [purchaseLoading, setPurchaseLoading] = useState(false);
    const [error, setError] = useState('');
    const [muxReady, setMuxReady] = useState(() => (typeof window !== 'undefined' && !!window.customElements?.get?.('mux-player')));

    const subscribedReturn = (typeof window !== 'undefined') && new URLSearchParams(window.location.search).get('subscribed') === 'true';

    // Derived viewer state.
    const clerkUser = demoMode ? null : user;
    const role = clerkUser?.publicMetadata?.role;
    const isSubscriber = !demoMode && hasActiveSubscription(clerkUser);
    const hasAccess = role === 'admin' || role === 'friend' || isSubscriber;
    const userEmail = clerkUser?.primaryEmailAddress?.emailAddress || '';

    // ── Load class metadata (paint-first; never gates the shell) ──
    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        fetch(`/api/public-class?id=${encodeURIComponent(id)}`)
            .then(async (res) => {
                if (cancelled) return;
                if (res.status === 404) { setStatus('notfound'); return; }
                if (!res.ok) { setStatus('error'); return; }
                const data = await res.json();
                setCls(data);
                setStatus('ready');
            })
            .catch(() => { if (!cancelled) setStatus('error'); });
        return () => { cancelled = true; };
    }, [id]);

    // ── Revalidate site copy (stale-while-revalidate; cached copy painted first) ──
    useEffect(() => {
        let cancelled = false;
        fetchSiteContent().then((c) => { if (!cancelled && c) setContent(c); }).catch(() => {});
        return () => { cancelled = true; };
    }, []);

    // ── Load Mux Player web component when we have a free class to play ──
    useEffect(() => {
        if (status !== 'ready' || !cls?.isFree || !cls?.muxPlaybackId) return;
        if (window.customElements?.get?.('mux-player')) { setMuxReady(true); return; }
        if (!document.querySelector(`script[src="${MUX_PLAYER_CDN}"]`)) {
            const script = document.createElement('script');
            script.src = MUX_PLAYER_CDN;
            script.async = true;
            document.head.appendChild(script);
        }
        let cancelled = false;
        window.customElements?.whenDefined?.('mux-player').then(() => { if (!cancelled) setMuxReady(true); }).catch(() => {});
        return () => { cancelled = true; };
    }, [status, cls?.isFree, cls?.muxPlaybackId]);

    // ── Member hand-off: members watch in the full app, always ──
    // The moment we know a signed-in viewer has access, send them into the
    // in-app player via the /workouts deep-link. `replace` so Back doesn't
    // bounce them here and re-trigger.
    useEffect(() => {
        if (demoMode) return;
        if (!isLoaded || !isSignedIn || !hasAccess) return;
        if (!id) return;
        navigate(`/workouts?recording=${encodeURIComponent(id)}`, { replace: true });
    }, [demoMode, isLoaded, isSignedIn, hasAccess, id, navigate]);

    // ── Post-subscribe: nudge Clerk to reflect the new subscription ──
    // After Stripe returns to /class/:id?subscribed=true the webhook may lag.
    // We reload the Clerk user a few times; once classSubscription flips true,
    // the hand-off effect above fires and carries them into the app.
    // A member can also subscribe from here. This page never renders the
    // welcome itself — it records the intent and lets the hand-off below carry
    // them to /workouts, which owns the single render site.
    useEffect(() => {
        if (demoMode || !subscribedReturn) return;
        // Signed-in only: a pasted ?subscribed=true URL must never queue the
        // welcome for someone who did not actually check out.
        if (!isLoaded || !isSignedIn) return;
        markWelcomePending();
    }, [demoMode, subscribedReturn, isLoaded, isSignedIn]);

    const subscribePollRef = useRef({ tries: 0, active: false });
    useEffect(() => {
        if (demoMode || !subscribedReturn) return;
        if (!isLoaded || !isSignedIn) return;
        if (hasAccess) return; // already reflected — hand-off effect will fire
        if (subscribePollRef.current.active) return;
        subscribePollRef.current.active = true;
        let cancelled = false;
        const poll = async () => {
            if (cancelled) return;
            try { if (clerkUser?.reload) await clerkUser.reload(); } catch { /* ignore */ }
            if (cancelled) return;
            subscribePollRef.current.tries += 1;
            if (!hasActiveSubscription(clerkUser) && subscribePollRef.current.tries < 8) {
                setTimeout(poll, 2500);
            }
        };
        poll();
        return () => { cancelled = true; };
        // clerkUser intentionally read live inside the loop (ref-free reload).
    }, [demoMode, subscribedReturn, isLoaded, isSignedIn, hasAccess]);

    // ── Account-first subscribe (identical call to every other Subscribe button) ──
    // `plan` is sent only when a plan choice was shown (or carried across the
    // sign-up modal) — otherwise the body is byte-identical to before.
    const handleSubscribe = async (storedPlan) => {
        if (purchaseLoading || demoMode) return;
        setPurchaseLoading(true);
        setError('');
        const plan = storedPlan || (annualOffer ? selectedPlan : null);
        try {
            const token = await getToken();
            const res = await fetch('/api/class-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ type: 'subscription', userEmail, returnClassId: id, ...(plan ? { plan } : {}) }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.sessionUrl) {
                throw new Error(data.error || "We couldn't start checkout. Please try again.");
            }
            window.location.href = data.sessionUrl;
        } catch (e) {
            setPurchaseLoading(false);
            setError(e.message || 'Something went wrong. Please try again.');
        }
    };

    // ── Auto-fire checkout after a signed-out viewer authenticates ──
    const justSignedUpRef = useRef(false);
    useEffect(() => {
        if (demoMode || !isLoaded || !isSignedIn) return;
        const hasIntent = justSignedUpRef.current || consumeFreshClassSubscribeIntent();
        if (hasIntent && !isSubscriber) {
            justSignedUpRef.current = false;
            clearClassSubscribeIntent();
            handleSubscribe(consumePlanChoice());
        }
        // handleSubscribe is a stable-enough closure reading live values; excluding
        // it avoids a redefinition loop (same pattern as JoinPage).
    }, [demoMode, isLoaded, isSignedIn, isSubscriber]);

    // ════════════════════════ RENDER ════════════════════════

    // Members (and admins/friends) are mid-redirect into the app.
    if (!demoMode && isLoaded && isSignedIn && hasAccess) {
        return <PageShell><CenteredNote label={copyFrom(content, 'classPageOpeningLabel')} /></PageShell>;
    }
    // Returned from checkout, waiting for the subscription to reflect.
    if (!demoMode && subscribedReturn && isSignedIn && !hasAccess) {
        return <PageShell><CenteredNote label={copyFrom(content, 'classPageActivatingLabel')} /></PageShell>;
    }

    // Hold the skeleton until BOTH the class metadata AND Clerk have resolved.
    // Gating on Clerk (isLoaded) prevents a member from flashing the public
    // watch/convert UI for a frame before the hand-off redirect fires — they go
    // straight from skeleton to "Opening your library…". The session splash
    // screen masks this brief wait for first-time visitors.
    if (status === 'loading' || (!demoMode && !isLoaded)) {
        return (
            <PageShell>
                <div style={{ width: '100%', paddingBottom: '56.25%', height: 0, borderRadius: 20, background: 'color-mix(in srgb, var(--mom-charcoal) 6%, transparent)' }} />
            </PageShell>
        );
    }

    if (status === 'notfound' || status === 'error') {
        return (
            <PageShell>
                <h1 style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 'clamp(24px,4vw,32px)', color: 'var(--mom-charcoal)', marginBottom: 10 }}>
                    {copyFrom(content, 'classPageUnavailableHeading')}
                </h1>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.6, marginBottom: 24 }}>
                    {copyFrom(content, 'classPageUnavailableBody')}
                </p>
                <button className="mom-btn mom-btn-primary" onClick={() => navigate('/join')}
                    style={{ padding: '14px 28px', borderRadius: 14, fontSize: 15, fontWeight: 600 }}>
                    {copyFrom(content, 'classPageExploreLabel')}
                </button>
            </PageShell>
        );
    }

    // status === 'ready' — class metadata loaded; viewer is logged-out or a
    // signed-in non-member (members were handed off above).
    const canPlayInline = cls.isFree && cls.muxPlaybackId;
    const joinHeading = copyFrom(content, canPlayInline ? 'classPageJoinHeading' : 'classPagePaidHeading');
    const joinBody = copyFrom(content, canPlayInline ? 'classPageJoinBody' : 'classPagePaidBody');
    const priceLabel = copyFrom(content, 'subscriptionPriceLabel');

    return (
        <PageShell>
            <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--mom-warm-gray)', marginBottom: 12 }}>
                {copyFrom(content, 'classPageEyebrow')}
            </p>

            {/* Player (free) or locked poster (paid) */}
            {canPlayInline
                ? (muxReady
                    ? <PlayerFrame playbackId={cls.muxPlaybackId} />
                    : <div style={{ width: '100%', paddingBottom: '56.25%', height: 0, borderRadius: 20, background: '#000' }} />)
                : <PosterFrame thumbnailUrl={cls.thumbnailUrl} title={cls.title} locked={!cls.isFree} />}

            <div style={{ marginTop: 22 }}>
                <MetaLine cls={cls} content={content} />
                <h1 style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontSize: 'clamp(24px,4.5vw,34px)', color: 'var(--mom-charcoal)', lineHeight: 1.2, marginBottom: 10 }}>
                    {cls.title || 'A class with Danielle'}
                </h1>
                {cls.description && (
                    <div style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.7 }}>
                        <FormattedText text={cls.description} />
                    </div>
                )}
            </div>

            {/* Conversion panel — account-first → subscribe */}
            <div style={{
                marginTop: 28, padding: 'clamp(22px,3vw,32px)', borderRadius: 22,
                background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(20px) saturate(1.4)', WebkitBackdropFilter: 'blur(20px) saturate(1.4)',
                border: '1px solid rgba(255,255,255,0.55)', boxShadow: 'var(--shadow-md)',
            }}>
                <h2 style={{ fontFamily: 'var(--mom-font-serif)', fontStyle: 'italic', fontWeight: 700, fontSize: 'clamp(20px,3vw,26px)', color: 'var(--mom-charcoal)', marginBottom: 8 }}>
                    {joinHeading}
                </h2>
                <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 15, color: 'var(--mom-warm-gray)', lineHeight: 1.6, marginBottom: 20 }}>
                    {joinBody}{!annualOffer && <> <span style={{ color: 'var(--mom-charcoal)', fontWeight: 600 }}>{priceLabel}.</span></>}
                </p>

                {annualOffer && (
                    <div style={{ marginBottom: 18 }}>
                        <PlanOptions
                            offer={annualOffer}
                            copy={resolvePlanCopy(content)}
                            value={selectedPlan}
                            onChange={setSelectedPlan}
                        />
                    </div>
                )}

                {error && (
                    <p style={{ fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-danger, #c46b6b)', marginBottom: 14 }}>{error}</p>
                )}

                {/* Signed-in non-member → subscribe directly. Signed-out → sign up
                    (modal) then auto-fire. Demo → route to /join (no real Clerk). */}
                {(!demoMode && isSignedIn) ? (
                    <button className="mom-btn mom-btn-primary" onClick={() => handleSubscribe()} disabled={purchaseLoading}
                        style={{ padding: '15px 32px', borderRadius: 14, fontSize: 16, fontWeight: 700, opacity: purchaseLoading ? 0.6 : 1 }}>
                        {purchaseLoading ? 'Starting checkout…' : copyFrom(content, 'classPageJoinCtaLabel')}
                    </button>
                ) : demoMode ? (
                    <button className="mom-btn mom-btn-primary" onClick={() => navigate('/join')}
                        style={{ padding: '15px 32px', borderRadius: 14, fontSize: 16, fontWeight: 700 }}>
                        {copyFrom(content, 'classPageJoinCtaLabel')}
                    </button>
                ) : (
                    <>
                        <SignUpButton mode="modal" appearance={signUpModalAppearance}>
                            <button className="mom-btn mom-btn-primary"
                                onClick={() => {
                                    justSignedUpRef.current = true;
                                    markClassSubscribeIntent();
                                    if (annualOffer) rememberPlanChoice(selectedPlan);
                                }}
                                style={{ padding: '15px 32px', borderRadius: 14, fontSize: 16, fontWeight: 700 }}>
                                {copyFrom(content, 'classPageJoinCtaLabel')}
                            </button>
                        </SignUpButton>
                        <div style={{ marginTop: 14 }}>
                            <SignInButton mode="modal">
                                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--mom-font-sans)', fontSize: 14, fontWeight: 600, color: 'var(--mom-link, var(--mom-primary-dark))', padding: 0 }}>
                                    {copyFrom(content, 'classPageSignInLabel')}
                                </button>
                            </SignInButton>
                        </div>
                    </>
                )}
            </div>
        </PageShell>
    );
}

export default ClassPage;
